import React, { useState, useRef, useEffect } from 'react';
import './SearchableMultiSelect.css';

const SearchableMultiSelect = ({ 
  options = [], 
  selectedValues = [], 
  onChange, 
  placeholder = "Chọn đơn vị...",
  searchPlaceholder = "Gõ để tìm kiếm..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Chuẩn hóa selected values sang string để so sánh ổn định
  const normalizedSelected = selectedValues.map(v => String(v));

  // Lọc options theo search term
  const filteredOptions = options.filter(option =>
    (option.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Đóng dropdown khi click bên ngoài
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus vào search input khi mở dropdown
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggleOption = (optionId) => {
    const id = String(optionId);
    let newSelectedValues = [...normalizedSelected];

    if (id === 'all') {
      newSelectedValues = ['all'];
    } else {
      // Bỏ 'all' nếu đang chọn
      newSelectedValues = newSelectedValues.filter(v => v !== 'all');
      if (newSelectedValues.includes(id)) {
        newSelectedValues = newSelectedValues.filter(v => v !== id);
      } else {
        newSelectedValues = [...newSelectedValues, id];
      }
    }

    onChange(newSelectedValues);
  };

  const handleRemoveOption = (optionId) => {
    const id = String(optionId);
    const newSelectedValues = normalizedSelected.filter(v => v !== id);
    onChange(newSelectedValues);
  };

  const getSelectedOptionsText = () => {
    if (normalizedSelected.length === 0) return placeholder;
    if (normalizedSelected.length === 1) {
      const only = normalizedSelected[0];
      if (only === 'all') return 'Tất cả địa điểm';
      const option = options.find(opt => String(opt.id) === only);
      return option ? option.name : placeholder;
    }
    return `Đã chọn ${normalizedSelected.length} đơn vị`;
  };

  return (
    <div className="searchable-multi-select" ref={dropdownRef}>
      {/* Selected items display (ẩn nếu đang là 'Tất cả địa điểm') */}
      {!(normalizedSelected.length === 1 && normalizedSelected[0] === 'all') && (
        <div className="selected-items">
          {normalizedSelected.filter(v => v !== 'all').map(valueId => {
            const option = options.find(opt => String(opt.id) === valueId);
            return option ? (
              <span key={valueId} className="selected-item">
                {option.name}
                <button
                  type="button"
                  className="remove-item"
                  onClick={() => handleRemoveOption(valueId)}
                >
                  ×
                </button>
              </span>
            ) : null;
          })}
        </div>
      )}

      {/* Dropdown trigger */}
      <div 
        className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="trigger-text">{getSelectedOptionsText()}</span>
        <span className="dropdown-arrow">▼</span>
      </div>

      {/* Dropdown content */}
      {isOpen && (
        <div className="dropdown-content">
          {/* Search input */}
          <div className="search-container">
            <input
              ref={searchInputRef}
              type="text"
              className="search-input"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options list */}
          <div className="options-list">
            {options.length === 0 ? (
              <div className="no-options">Đang tải danh sách đơn vị...</div>
            ) : filteredOptions.length === 0 ? (
              <div className="no-options">Không tìm thấy đơn vị nào</div>
            ) : (
              filteredOptions.map(option => {
                const id = String(option.id);
                const isSelected = normalizedSelected.includes(id) || (id === 'all' && normalizedSelected[0] === 'all');
                return (
                  <div
                    key={option.id}
                    className={`option-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleToggleOption(option.id)}
                  >
                    <span className="option-text">{option.name || `Đơn vị ${option.id}`}</span>
                    {isSelected && (
                      <span className="check-mark">✓</span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableMultiSelect;
